import { createFeatureSelector, createSelector } from '@ngrx/store';

import { getUnreadValue } from 'app/inbox/sorting-btn/store/delivery-sort.selectors';
import * as fromOffline from '../reducers/offline.reducer';
import { OfflineDelivery } from '../../models/delivery.model';
import { HelperService } from '../../services/helper.service';
import { DeliveryAsset } from '../../models';

export const selectOffline = createFeatureSelector<fromOffline.OfflineState>(
  fromOffline.offlineFeatureKey,
);

export const selectOfflinedeliveriesWithoutBasePath = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState) => (state.deliveries || []).map((delivery: OfflineDelivery) =>
      HelperService.cloneOfflineDelivery(delivery),
    ),
);

export const selectOfflineDeliveries = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState) => {
    const { basePath } = state;
    return (state.deliveries || []).map((delivery: OfflineDelivery) =>
      getDeliveryWithBasePath(delivery, basePath),
    );
  },
);

export const selectDownloadedDeliveries = createSelector(
  selectOffline,
  getUnreadValue,
  (state: fromOffline.OfflineState, listStatus) => {
    const { basePath } = state;
    const downloadedDeliveries = state.deliveries
      .filter((delivery: OfflineDelivery) => {
        if (listStatus === 'unread') {
          return !delivery.openedDelivery;
        }
        return true;
      })
      .filter(downloadedDeliveriesFilter);

    return (downloadedDeliveries || []).map((delivery: OfflineDelivery) =>
      getDeliveryWithBasePath(delivery, basePath),
    );
  },
);

export const selectDownloadedCount = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState) => {
    const deliveries = state.deliveries.filter(downloadedDeliveriesFilter);

    return deliveries?.length ?? 0;
  },
);

export const selectOfflineState = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState) => state.isOffline,
);

export const selectOfflineDelivery = createSelector(
  selectOfflineDeliveries,
  (deliveries, props) => {
    const { deliveryKeyCode } = props;
    const currentDelivery: OfflineDelivery = deliveries.find(
      (delivery: OfflineDelivery) => delivery.deliveryKeyCode === deliveryKeyCode,
    );

    return currentDelivery
      ? HelperService.cloneOfflineDelivery(currentDelivery)
      : <OfflineDelivery>{};
  },
);

export const selectDownloadedDelivery = createSelector(
  selectDownloadedDeliveries,
  (deliveries, props) => {
    const { deliveryKeyCode } = props;
    const currentDelivery: OfflineDelivery = deliveries.find(
      (delivery: OfflineDelivery) => delivery.deliveryKeyCode === deliveryKeyCode,
    );

    return currentDelivery
      ? HelperService.cloneOfflineDelivery(currentDelivery)
      : <OfflineDelivery>{};
  },
);

export const selectOfflineDeliveryAudioAssets = createSelector(
  selectOfflineDeliveries,
  (deliveries, props) => {
    const { deliveryKeyCode } = props;
    const currentDelivery: OfflineDelivery = deliveries.find(
      (delivery: OfflineDelivery) => delivery.deliveryKeyCode === deliveryKeyCode,
    );

    if (currentDelivery) {
      return HelperService.cloneOfflineDelivery(currentDelivery).offlineAudioAssets;
    }

    return [];
  },
);

export const selectCacheDisabled = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState) => !state.cacheEnabled,
);

export const selectNetworkType = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState) => state.networkType,
);

export const selectIsAllowToDownload = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState) => {
    const { networkType, wifiOnly } = state;

    return wifiOnly ? networkType === 'wifi' : true;
  },
);

export const selectDownloadConfig = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState) => {
    const { isOffline, wifiOnly, cacheEnabled, networkType } = state;

    return {
      isOffline,
      wifiOnly,
      cacheEnabled,
      networkType,
    };
  },
);

export const selectIsAllowWifiDownload = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState) => {
    const { networkType } = state;

    return networkType === 'wifi';
  },
);

export const selectAllDeliverySize = createSelector(
  selectDownloadedDeliveries,
  (deliveries: OfflineDelivery[]) => {
    const totalMb =
      deliveries.reduce((accumulator: number, currentDelivery: OfflineDelivery) => (
        accumulator + (currentDelivery.offlineCacheDeliverySize || 0)
      ), 0) / 1048576; // get MB from bytes

    return totalMb.toFixed(2);
  },
);

export const selectIsDamagedOfflineAssets = createSelector(
  selectOffline,
  (state: fromOffline.OfflineState, props) => {
    const { deliveries, isOffline } = state;
    let isDamaged = false;
    let hasCompleted = false;
    const { deliveryKeyCode } = props;
    const currentDelivery: OfflineDelivery = deliveries.find(
      (delivery: OfflineDelivery) => delivery.deliveryKeyCode === deliveryKeyCode,
    );

    if (currentDelivery) {
      currentDelivery.offlineAudioAssets.forEach((asset: DeliveryAsset) => {
        if (asset.offlineDownloadStatus !== 'COMPLETED') {
          isDamaged = true;
        }

        if (asset.offlineDownloadStatus === 'COMPLETED') {
          hasCompleted = true;
        }
      });
    }

    return !isOffline && isDamaged && hasCompleted;
  },
);

export const selectIsDownloading = createSelector(
  selectOffline,
  (offlineState: fromOffline.OfflineState) => offlineState.isDownloading,
);

export const selectDownloadingDelivery = createSelector(
  selectOfflineDeliveries,
  (deliveries: OfflineDelivery[], props) => {
    const { deliveryKeyCode } = props;
    const delivery = deliveries.find(
      (offlineDelivery: OfflineDelivery) =>
        offlineDelivery &&
        offlineDelivery.offlineStatus === 'downloading' &&
        offlineDelivery.deliveryKeyCode !== deliveryKeyCode,
    );
    return delivery || {};
  },
);

function getDeliveryWithBasePath(offlineDelivery, basePath): OfflineDelivery {
  const delivery = HelperService.cloneOfflineDelivery(offlineDelivery);

  delivery.deliveryImage = addBasePath(delivery.deliveryImage, basePath);
  delivery.mobileDeliveryImage =
    (delivery.mobileDeliveryImage && addBasePath(delivery.mobileDeliveryImage, basePath)) ||
    delivery.deliveryImage;

  delivery.offlineAudioAssets = (delivery.offlineAudioAssets || []).map((asset: DeliveryAsset) => {
    asset.types = (asset.types || []).map((typeTrack: any) => {
      typeTrack.url = addBasePath(typeTrack.url, basePath);
      return typeTrack;
    });
    return asset;
  });

  return delivery;
}

function addBasePath(url = '', basePath = '') {
  if (!basePath || url.includes('http') || url.includes('file://')) {
    return url;
  }
  return `${basePath}${url}`;
}

function downloadedDeliveriesFilter(delivery: OfflineDelivery) {
  let res = false;
  if (delivery) {
    if (delivery.offlineStatus === 'downloaded') {
      return true;
    }

    (delivery.offlineAudioAssets || []).forEach((offlineAsset: DeliveryAsset) => {
      if (offlineAsset.offlineDownloadStatus === 'COMPLETED') {
        res = true;
      }
    });
  }
  return res;
}
